import { FC, useEffect, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { Bugo } from '@shared/api/bugo/bugo.interface';
import { Fevent } from '@shared/api/fevent/fevent.interface';
import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { Member } from '@shared/api/member/member.interface';
import { User } from '@shared/api/user/user.interface';
import { Loading } from '@shared/components/Loading';
import SearchBar from '@shared/containers/SearchBar/SearchBar';
import { searchTextAtomFamily } from '@shared/containers/SearchBar/state/searchBar.atom';
import { api_ } from '@shared/plugIn/axios';
import { formatDate, formatFeventDateString } from '@shared/utils/formatDate';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';

import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

/**
 * 관리자용 답례글(답례인사) 관리 페이지
 *
 * 부고관리 리스트는 파기(meta.isDeleted)된 부고를 아예 제외하지만,
 * 답례글은 파기 이후에도 상주분이 다시 요청하는 경우가 있어 admin은 조회할 수 있어야 한다.
 * 대신 전체 목록을 뿌리지 않고 고인명/상주명 검색으로만 접근하게 한다.
 */

const SEARCH_TEXT_ID = 'courtesyManagePage';
const SEARCH_LIMIT = 50;

//* 답례글 발송여부는 상주별 bugo에 남는다. (bugo 스키마 timestamps: updatedAt 존재)
type CourtesyBugo = Bugo & { updatedAt?: Date };

type CourtesyMember = Member<string, CourtesyBugo>;

type CourtesyFevent = Fevent<
  User,
  FuneralHomeInfo,
  undefined,
  undefined,
  CourtesyMember[]
>;

const FEVENT_POPULATE = [
  { path: 'funeralHomeInfo' },
  { path: 'memberOrderList', populate: { path: 'bugo' } },
  { path: 'user' },
  { path: 'bugoBrand' },
];

const escapeRegex = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

//* 저장형식(01012345678 / 010-1234-5678 / +82 10-...)과 무관하게 매칭되는 패턴.
//* 백엔드 phone-normalize.ts와 같은 규칙. 숫자 4자리 미만이면 전화번호 검색을 하지 않는다.
const PHONE_SEARCH_MIN_DIGITS = 4;

const buildPhoneRegexPattern = (input: string): string | null => {
  const digits = input.replace(/\D/g, '');
  if (digits.length < PHONE_SEARCH_MIN_DIGITS) return null;

  const candidates = new Set<string>([digits]);
  if (digits.startsWith('0')) candidates.add('82' + digits.slice(1));
  if (digits.startsWith('82') && digits.length >= 11)
    candidates.add('0' + digits.slice(2));

  return Array.from(candidates)
    .map((candidate) => candidate.split('').join('\\D*'))
    .join('|');
};

//* 정규식 백슬래시가 그대로 실려나가지 않도록 쿼리를 직접 인코딩한다.
const adminQuery = (query: {
  filter?: unknown;
  populate?: unknown;
  options?: unknown;
  select?: string;
}) =>
  Object.entries(query)
    .filter(([, value]) => value !== undefined)
    .map(
      ([key, value]) =>
        `${key}=${encodeURIComponent(
          typeof value === 'string' ? value : JSON.stringify(value),
        )}`,
    )
    .join('&');

//* 고인명 검색 + 상주명·상주 연락처 검색(멤버 → 부고) 결과를 합쳐서 반환
const searchFeventList = async (keyword: string): Promise<CourtesyFevent[]> => {
  const regex = { $regex: escapeRegex(keyword) };
  const phonePattern = buildPhoneRegexPattern(keyword);
  const options = { sort: { createdAt: -1 }, limit: SEARCH_LIMIT };

  const memberFilter = phonePattern
    ? { $or: [{ fullName: regex }, { phoneNumber: { $regex: phonePattern } }] }
    : { fullName: regex };

  const [byDeceased, byMemberName] = await Promise.all([
    api_.get<CourtesyFevent[]>(
      `/admin/fevent/?${adminQuery({
        filter: { 'deceasedInfo.name': regex },
        populate: FEVENT_POPULATE,
        options,
      })}`,
    ),
    api_.get<{ _id: string; fevent?: string }[]>(
      `/admin/member/?${adminQuery({
        filter: memberFilter,
        options,
        select: 'fevent',
      })}`,
    ),
  ]);

  const feventIdList = Array.from(
    new Set(
      byMemberName.data
        .map((member) => member.fevent)
        .filter((feventId): feventId is string => Boolean(feventId)),
    ),
  );

  const byMember = feventIdList.length
    ? await api_.get<CourtesyFevent[]>(
        `/admin/fevent/?${adminQuery({
          filter: { _id: { $in: feventIdList } },
          populate: FEVENT_POPULATE,
          options,
        })}`,
      )
    : { data: [] as CourtesyFevent[] };

  const merged = new Map<string, CourtesyFevent>();
  [...byDeceased.data, ...byMember.data].forEach((fevent) => {
    merged.set(fevent._id, fevent);
  });

  //* 항상 최신 등록순(createdAt 내림차순)
  return Array.from(merged.values()).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
};

const getAgencyName = (fevent: CourtesyFevent): string | undefined => {
  const bugoBrand = fevent.bugoBrand as unknown as
    | { bugoAgency?: { nickName?: string; corpName?: string } }
    | undefined;
  return bugoBrand?.bugoAgency?.nickName ?? bugoBrand?.bugoAgency?.corpName;
};

//* 자동발송 예정시각: 발인 다음날 오전 9시 (답례글 전송 페이지와 같은 규칙)
const courtesyScheduledAt = (fevent: CourtesyFevent): Date | null => {
  const coffinOutDate = fevent.deceasedInfo?.coffinOut?.date;
  if (!coffinOutDate) return null;
  const scheduled = new Date(coffinOutDate);
  scheduled.setDate(scheduled.getDate() + 1);
  scheduled.setHours(9, 0, 0, 0);
  return scheduled;
};

const formatDateTime = (date: Date) =>
  formatDate(date, {
    dateSeparater: '.',
    contains: { hours: true, minutes: true },
  });

/**
 * 답례글 발송 상태.
 *
 * fevent.meta.courtesySend / willCourtesySend는 서버에서 채우는 곳이 없어 쓰지 않는다.
 * 실제 발송 기록은 상주별 bugo.schedule.courtesySend에 남고, 별도 발송시각 필드가
 * 없어 해당 bugo 문서의 updatedAt(플래그가 켜진 시점)을 발송시각으로 본다.
 */
const getCourtesyState = (fevent: CourtesyFevent) => {
  const memberList = fevent.memberOrderList ?? [];
  const sentList = memberList.filter((member) => member.bugo?.schedule?.courtesySend);
  const sentAtList = sentList
    .map((member) => member.bugo?.updatedAt)
    .filter((date): date is Date => Boolean(date))
    .map((date) => new Date(date).getTime());

  return {
    total: memberList.length,
    sentCount: sentList.length,
    lastSentAt: sentAtList.length > 0 ? new Date(Math.max(...sentAtList)) : null,
    scheduledAt: courtesyScheduledAt(fevent),
  };
};

//* 부고관리(BugoCard)와 같은 틀. 답례글 관련 정보/링크만 붙인다.
const CourtesyCard: FC<{ fevent: CourtesyFevent }> = function CourtesyCard({ fevent }) {
  const agencyName = getAgencyName(fevent);
  const funeralHomeInfo = fevent.funeralHomeInfoEmbed ?? fevent.funeralHomeInfo;
  const roomName = fevent.roomInfoEmbed?.name;
  const memberList = fevent.memberOrderList ?? [];
  const { total, sentCount, lastSentAt, scheduledAt } = getCourtesyState(fevent);
  const courtesySent = sentCount > 0;

  const isMobile = !useMediaQuery({
    query: '(min-width: 1024px)',
  });

  return (
    <div className="space-y-2 rounded-2xl p-2 shadow-smd theme-text-1 theme-bg-1">
      {/* 1st row */}
      <div className="flex items-center justify-between">
        <div className="flex gap-4 pl-1">
          {/* 고인태그 */}
          <div className="bugo-tag w-fit break-keep text-white theme-bg-14">
            {!isMobile && <span>故</span>} {fevent.deceasedInfo?.name}
          </div>
          {/* 상주태그 */}
          {memberList[0]?.fullName && (
            <div className="bugo-tag w-fit text-black theme-bg-5">
              {!isMobile && <span>상주&nbsp;</span>}
              <span className="break-keep font-bold">{memberList[0]?.fullName}</span>
            </div>
          )}
        </div>
        <div className="center-box gap-2">
          {fevent.meta?.isDeleted && (
            <div className="bugo-tag break-keep bg-warn text-white">파기됨</div>
          )}
          {agencyName && <div className="bugo-tag break-keep border">{agencyName}</div>}
          {fevent.user?.info?.name && (
            <div className="bugo-tag w-fit rounded-none bg-gray-100 text-black">
              {!isMobile && <span>지도사&nbsp;</span>}
              <span className="break-keep font-bold">{fevent.user.info.name}</span>
            </div>
          )}
        </div>
      </div>
      {/* 2nd row: funeralHome info */} {/* 3rd row: 발인 / 답례글 상태 */}
      <div
        className="grid items-center gap-y-1 px-4 pb-1 text-12"
        style={{
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 0.4fr)',
        }}
      >
        <div>
          <p className="leading-5">{funeralHomeInfo?.name}</p>
        </div>
        <div>
          <p className="break-keep leading-5">{roomName}</p>
        </div>
        <div>
          <p className="break-keep leading-5">
            발인 {formatFeventDateString(fevent.deceasedInfo?.coffinOut)}
          </p>
        </div>
        <div>
          <p className="break-keep leading-5 theme-text-8">상주 {total}명</p>
        </div>
      </div>
      {/* 답례글 발송 상태 */}
      <div className="flex items-start gap-1.5 px-4 pb-1 text-12">
        <span
          className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
            courtesySent ? 'bg-blue-500' : scheduledAt ? 'bg-amber-400' : 'bg-gray-300'
          }`}
        />
        <p className="break-keep leading-5">
          {courtesySent ? (
            <>
              <span className="font-bold">
                답례글 발송 {sentCount}/{total}
              </span>
              {lastSentAt && (
                <span className="pl-1 theme-text-8">{formatDateTime(lastSentAt)}</span>
              )}
            </>
          ) : (
            <>
              <span className="font-bold theme-text-8">답례글 미발송</span>
              {scheduledAt && (
                <span className="pl-1 theme-text-8">
                  발송예정 {formatDateTime(scheduledAt)}
                </span>
              )}
            </>
          )}
        </p>
      </div>
      {/* 상주별 답례글 (연락처는 표기하지 않는다) */}
      <div className="px-1">
        {memberList.length === 0 ? (
          <p className="px-3 text-12 theme-text-8">등록된 상주가 없습니다.</p>
        ) : (
          <div className="grid grid-cols-2 gap-1">
            {memberList.map((member) => (
              <div
                key={member._id}
                className="space-y-1 rounded-md border p-2 theme-border-1"
              >
                <p className="truncate text-12 font-bold leading-5">
                  {member.fullName}
                  {member.relationType ? (
                    <span className="pl-1 font-normal theme-text-8">
                      {member.relationType}
                    </span>
                  ) : null}
                </p>
                <p className="truncate text-11 leading-4 theme-text-8">
                  {member.bugo?.schedule?.courtesySend
                    ? `발송 ${
                        member.bugo.updatedAt
                          ? formatDate(member.bugo.updatedAt, {
                              dateSeparater: '.',
                              contains: { year: false },
                            })
                          : ''
                      }`
                    : '미발송'}
                </p>
                <div className="flex gap-1">
                  <Link
                    to={`/member/${member._id}/courtesy`}
                    className="flex-1 rounded border py-1 text-center text-11 font-bold theme-border-1"
                  >
                    답례글
                  </Link>
                  <Link
                    to={`/member/${member._id}/courtesy/preview`}
                    className="flex-1 rounded border py-1 text-center text-11 font-bold theme-text-8 theme-border-1"
                  >
                    미리보기
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* 부고단위 액션 */}
      <div className="px-1 pb-1">
        <Link
          to={`/fevent/${fevent._id}/courtesy-send`}
          className="block rounded-md py-2 text-center text-12 font-bold text-white theme-bg-main"
        >
          답례글 링크 발송
        </Link>
      </div>
    </div>
  );
};

const AdminCourtesyManagePage: FC = function AdminCourtesyManagePage() {
  useAdminTitleHook('답례 관리', undefined, undefined, true, '/admin');

  const searchText = useRecoilValue(searchTextAtomFamily(SEARCH_TEXT_ID));
  const [result, setResult] = useState<CourtesyFevent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  //* 늦게 도착한 이전 검색 응답이 최신 결과를 덮어쓰지 않도록
  const searchSeqRef = useRef(0);

  useEffect(() => {
    const keyword = searchText.trim();
    const seq = ++searchSeqRef.current;

    if (keyword.length === 0) {
      setResult(null);
      setError(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    searchFeventList(keyword)
      .then((feventList) => {
        if (seq !== searchSeqRef.current) return;
        setResult(feventList);
      })
      .catch((e) => {
        if (seq !== searchSeqRef.current) return;
        console.error(e);
        setResult([]);
        setError(true);
      })
      .finally(() => {
        if (seq !== searchSeqRef.current) return;
        setLoading(false);
      });
  }, [searchText]);

  return (
    <div className="px-4 pb-10">
      {/* Search Bar */}
      <div className="space-y-2 px-2 py-4">
        <SearchBar
          searchTextAtomId={SEARCH_TEXT_ID}
          placeholder="고인명/상주명/상주 연락처로 검색하세요."
        />
      </div>

      {loading && (
        <div className="center-box h-40">
          <Loading />
        </div>
      )}

      {!loading && error && (
        <div className="center-box h-40">
          <p className="text-sm theme-text-6">검색 중 오류가 발생했습니다.</p>
        </div>
      )}

      {!loading && !error && result?.length === 0 && (
        <div className="center-box h-40">
          <p className="text-sm theme-text-6">검색 결과가 없습니다.</p>
        </div>
      )}

      {!loading && result && result.length > 0 && (
        <div className="space-y-3">
          {result.map((fevent) => (
            <CourtesyCard fevent={fevent} key={'courtesycard' + fevent._id} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCourtesyManagePage;
